import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { motion } from 'motion/react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Вариант кнопки
   * @default 'primary'
   */
  variant?: ButtonVariant
  /**
   * Размер кнопки
   * @default 'md'
   */
  size?: ButtonSize
  /**
   * Состояние загрузки
   */
  loading?: boolean
  /**
   * Полная ширина кнопки
   */
  fullWidth?: boolean
  /**
   * Иконка слева
   */
  leftIcon?: ReactNode
  /**
   * Иконка справа
   */
  rightIcon?: ReactNode
  /**
   * Callback для haptic feedback при нажатии
   */
  onHapticFeedback?: (type?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
}

/**
 * UI компонент: Кнопка
 * 
 * Базовый компонент кнопки с поддержкой различных вариантов стилей,
 * размеров, состояний и haptic feedback для Telegram Mini App.
 * 
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Нажми меня
 * </Button>
 * 
 * <Button variant="secondary" loading>
 *   Загрузка...
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      onHapticFeedback,
      className,
      children,
      disabled,
      onClick,
      ...props
    },
    ref
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (loading || disabled) return

      onHapticFeedback?.('light')

      onClick?.(e)
    }

    const baseStyles = cn(
      // Базовые стили
      'inline-flex items-center justify-center',
      'rounded-xl', // Скругления 16px для единого стиля
      'font-medium transition-all duration-150',
      'focus:outline-none',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
      'touch-manipulation', // Оптимизация для touch
      'select-none', // Предотвращение выделения текста
      // Минимальный touch target
      'min-h-[44px] min-w-[44px]',
      // Варианты
      {
        // Primary - янтарно-оранжевая с градиентом 2-3%
        'bg-gradient-to-r from-primary-light to-primary-dark text-white hover:from-primary hover:to-primary-dark active:from-primary-dark active:to-primary-darker':
          variant === 'primary',
        // Dark theme Primary - адаптивный янтарно-оранжевый
        'dark:from-primary-dark dark:to-primary-darker dark:hover:from-primary dark:hover:to-primary-dark':
          variant === 'primary',
        // Secondary - стеклянная с белым 12-18%
        'bg-white/12 backdrop-blur-sm border-2 border-white/18 text-primary hover:bg-white/18 active:bg-white/24':
          variant === 'secondary',
        // Dark theme Secondary - стеклянная
        'dark:bg-white/12 dark:backdrop-blur-sm dark:border-white/18 dark:text-primary-light dark:hover:bg-white/18':
          variant === 'secondary',
        // Tertiary - текстовая
        'bg-transparent text-primary hover:bg-primary/10 active:bg-primary/20':
          variant === 'tertiary',
        // Dark theme Tertiary
        'dark:text-primary-light dark:hover:bg-white/10':
          variant === 'tertiary',
        // Ghost
        'bg-transparent text-gray-600 hover:bg-gray-100 active:bg-gray-200':
          variant === 'ghost',
        // Dark theme Ghost
        'dark:text-gray-400 dark:hover:bg-white/10 dark:active:bg-white/15':
          variant === 'ghost',
      },
      // Размеры
      {
        'h-[44px] px-5 text-sm': size === 'sm',
        'h-[48px] px-6 text-base': size === 'md',
        'h-[56px] px-8 text-lg': size === 'lg',
      },
      // Полная ширина
      {
        'w-full': fullWidth,
      },
      className
    )

    // Exclude drag-related props that conflict with motion.button
    const { onDrag, onDragStart, onDragEnd, ...restProps } = props as any

    return (
      <motion.button
        ref={ref}
        type="button"
        className={baseStyles}
        disabled={disabled || loading}
        onClick={handleClick}
        whileTap={!disabled && !loading ? { scale: 0.97 } : undefined}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
        {...restProps}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            <span className="sr-only">Загрузка...</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="mr-2">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="ml-2">{rightIcon}</span>}
          </>
        )}
      </motion.button>
    )
  }
)

Button.displayName = 'Button'
